import React, { useEffect, useRef, useState } from 'react';
import { bool, func } from 'prop-types';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import { InlineTextButton, Modal, NamedLink } from '../../components';

import css from './WelcomeTutorialsModal.module.css';

export const WELCOME_TUTORIALS_STORAGE_KEY = 'resuelvepr.welcomeTutorialsModalShown';

let shownWithoutSessionStorage = false;

const hasBeenShown = () => {
  if (shownWithoutSessionStorage) {
    return true;
  }

  try {
    return window.sessionStorage.getItem(WELCOME_TUTORIALS_STORAGE_KEY) === 'true';
  } catch (error) {
    return shownWithoutSessionStorage;
  }
};

const markAsShown = () => {
  try {
    window.sessionStorage.setItem(WELCOME_TUTORIALS_STORAGE_KEY, 'true');
  } catch (error) {
    shownWithoutSessionStorage = true;
  }
};

/**
 * Welcome modal that points first-time visitors in a browser tab to the tutorials.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isScrollingDisabled whether another component is blocking page scrolling
 * @param {Function} props.onManageDisableScrolling handler for managing page scrolling
 * @returns {JSX.Element} Welcome tutorials modal
 */
const WelcomeTutorialsModal = props => {
  const { isScrollingDisabled, onManageDisableScrolling } = props;
  const intl = useIntl();
  const dialogRef = useRef(null);
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    if (!isScrollingDisabled && !hasBeenShown()) {
      markAsShown();
      setOpen(true);
    }
  }, [isScrollingDisabled]);

  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus();
    }
  }, [isOpen]);

  const close = () => setOpen(false);
  const titleId = 'WelcomeTutorialsModal-title';
  const descriptionId = 'WelcomeTutorialsModal-description';

  return (
    <Modal
      id="WelcomeTutorialsModal"
      scrollLayerClassName={css.scrollLayer}
      containerClassName={css.modal}
      contentClassName={css.content}
      isOpen={isOpen}
      onClose={close}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
    >
      <div
        className={css.dialog}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex="-1"
      >
        <h2 className={css.title} id={titleId}>
          <FormattedMessage id="WelcomeTutorialsModal.title" />
        </h2>
        <p className={css.description} id={descriptionId}>
          <FormattedMessage id="WelcomeTutorialsModal.description" />
        </p>
        <div className={css.actions}>
          <NamedLink
            className={css.primaryLink}
            name="CMSPage"
            params={{ pageId: 'como-funciona' }}
            to={{ hash: '#tutoriales' }}
            onClick={close}
          >
            <FormattedMessage id="WelcomeTutorialsModal.primaryAction" />
          </NamedLink>
          <InlineTextButton className={css.secondaryButton} type="button" onClick={close}>
            <FormattedMessage id="WelcomeTutorialsModal.secondaryAction" />
          </InlineTextButton>
        </div>
      </div>
    </Modal>
  );
};

WelcomeTutorialsModal.propTypes = {
  isScrollingDisabled: bool,
  onManageDisableScrolling: func.isRequired,
};

export default WelcomeTutorialsModal;
